import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import SessionIdleGuard from "@/components/SessionIdleGuard";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import SeleccionarContextoAcceso from "@/pages/SeleccionarContextoAcceso";
import Layout from "@/pages/Layout";
import RecuperacionExtravio from "@/pages/RecuperacionExtravio";
import RegistroPublico from "@/pages/RegistroPublico";
import LegalDocumentosHub from "@/pages/legal/LegalDocumentosHub";
import ResetPassword from "@/pages/ResetPassword";
import EmailOAuthResultPage from "@/pages/despacho/EmailOAuthResultPage";
import { Loader2 } from "lucide-react";
import { buildProtectedRouteChildren } from "@/modules/main_ui";
import { VISOR_MODULE_DISPLAY_NAME } from "@/modules/workModuleCore";
import ErrorBoundary from "@/components/ErrorBoundary";
import { resolvePostLoginPath } from "@/lib/postLoginRedirect";
import { ACCESS_CONTEXT_SELECTION_PATH } from "@/lib/workspaceAccessContext";
import BntAsistenteCompact from "@/pages/asistente/BntAsistenteCompact";
import { BNT_ASISTENTE_PATH } from "@/modules/asistente/bntAsistenteRoutes";
import PortalMaterialidadHomePage from "@/pages/portalMaterialidad/PortalMaterialidadHomePage";
import PortalMaterialidadLoginPage from "@/pages/portalMaterialidad/PortalMaterialidadLoginPage";
import PortalMaterialidadCompaniesPage from "@/pages/portalMaterialidad/PortalMaterialidadCompaniesPage";
import PortalMaterialidadCompanyRequestsPage from "@/pages/portalMaterialidad/PortalMaterialidadCompanyRequestsPage";
import PortalMaterialidadRequestPage from "@/pages/portalMaterialidad/PortalMaterialidadRequestPage";
import PortalMaterialidadChangePasswordPage from "@/pages/portalMaterialidad/PortalMaterialidadChangePasswordPage";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#0F2D5C] mx-auto"/>
          <div className="text-sm text-slate-600 font-medium">Iniciando {VISOR_MODULE_DISPLAY_NAME}…</div>
          <div className="text-xs text-slate-400">Conectando al servidor</div>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.needs_trial_registration && location.pathname !== "/registro-inicial") {
    return <Navigate to="/registro-inicial" replace />;
  }
  if (
    user.requires_context_selection
    && location.pathname !== ACCESS_CONTEXT_SELECTION_PATH
  ) {
    return <Navigate to={ACCESS_CONTEXT_SELECTION_PATH} replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500"/></div>;
  if (user) {
    return <Navigate to={resolvePostLoginPath(user)} replace />;
  }
  return children;
}

function SessionIdleGuardBridge({ children }) {
  const { user, sessionRemember, logout } = useAuth();
  const location = useLocation();
  return (
    <SessionIdleGuard
      user={user}
      sessionRemember={sessionRemember}
      logout={logout}
      pathname={location.pathname}
    >
      {children}
    </SessionIdleGuard>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login/></PublicOnly>} />
      <Route path="/registro" element={<PublicOnly><RegistroPublico/></PublicOnly>} />
      <Route path="/seleccionar-contexto" element={<Protected><SeleccionarContextoAcceso/></Protected>} />
      <Route path="/legal/documentos" element={<LegalDocumentosHub/>} />
      <Route path="/recuperacion-extravio" element={<PublicOnly><RecuperacionExtravio/></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword/>} />
      <Route path="/correo-oauth/result" element={<EmailOAuthResultPage/>} />
      {/* Portal de Clientes Materialidad — fuera del Layout del despacho */}
      <Route path="/portal-materialidad" element={<PortalMaterialidadHomePage />} />
      <Route path="/portal-materialidad/login" element={<PortalMaterialidadLoginPage />} />
      <Route path="/portal-materialidad/empresas" element={<PortalMaterialidadCompaniesPage />} />
      <Route path="/portal-materialidad/empresas/:clientId" element={<PortalMaterialidadCompanyRequestsPage />} />
      <Route path="/portal-materialidad/solicitudes/:requestId" element={<PortalMaterialidadRequestPage mode="auth" />} />
      <Route path="/portal-materialidad/solicitud/:token" element={<PortalMaterialidadRequestPage mode="secure" />} />
      <Route path="/portal-materialidad/cambiar-password" element={<PortalMaterialidadChangePasswordPage />} />
      <Route
        path={BNT_ASISTENTE_PATH.replace(/^\//, "")}
        element={(
          <Protected>
            <BntAsistenteCompact />
          </Protected>
        )}
      />
      <Route path="/" element={<Protected><Layout/></Protected>}>
        {buildProtectedRouteChildren()}
      </Route>
      <Route path="*" element={<Navigate to="/" replace/>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <SessionIdleGuardBridge>
              <ErrorBoundary>
                <AppRoutes/>
              </ErrorBoundary>
              <Toaster position="top-right" richColors />
            </SessionIdleGuardBridge>
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}
